import { gsap } from "gsap";
import { module as BaseModule } from "modujs";

export default class extends BaseModule {
  constructor(m) {
    super(m);
    
    this.isOpen = false;
    this.timeline = null;
    this.focusableElements = [];
    this.previouslyFocusedElement = null;
    
    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
  }

  init() {
    this.setupModal();
    this.bindEvents();
    this.bindTriggers();
  }

  setupModal() {
    // Get modal elements
    this.modal = this.el;
    this.wrapper = this.modal.querySelector('[data-gallery-wrapper]') || this.modal.querySelector('[data-modal-wrapper]') || this.modal.firstElementChild;
    this.closeButton = this.modal.querySelector('.style-button');
    
    // Set initial state
    this.modal.style.display = 'none';
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    
    // Get focusable elements within the modal
    this.focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    // Set up initial GSAP timeline
    this.setupTimeline();
  }

  setupTimeline() {
    this.timeline = gsap.timeline({ paused: true });
    
    // Set initial states
    gsap.set(this.modal, { 
      opacity: 0,
      display: 'none'
    });
    
    gsap.set(this.wrapper, { 
      opacity: 0,
      y: 20
    });
    
    // Create timeline
    this.timeline
      .set(this.modal, { display: 'flex' })
      .to(this.modal, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out"
      })
      .to(this.wrapper, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        ease:  "power2.out"
      }, "-=0.1");
  }

  bindEvents() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.close);
    }
    
    // Escape key
    document.addEventListener('keydown', this.handleKeyDown);
    
    // Backdrop click
    this.modal.addEventListener('click', this.handleBackdropClick);
    
    // Prevent wrapper clicks from closing modal
    if (this.wrapper) {
      this.wrapper.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }

  bindTriggers() {
    // Find all trigger elements that should open this modal using data-modal-trigger-id
    const modalId = this.modal.id;
    
    this.triggers = [];
    const triggerElements = document.querySelectorAll(`[data-modal-trigger-id="${modalId}"]`);
    
    triggerElements.forEach(element => {
      this.triggers.push(element);
      element.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });
  }

  open() {
    if (this.isOpen) return;
    
    this.isOpen = true;
    
    // Store previously focused element
    this.previouslyFocusedElement = document.activeElement;
    
    // Update ARIA attributes
    this.modal.setAttribute('aria-hidden', 'false');
    
    // Play opening animation
    this.timeline.play();
    
    // Focus management
    this.trapFocus();
    
    // Stop Lenis scrolling
    if (window.lenis) {
      window.lenis.stop();
    }
    
    // Dispatch custom event
    const event = new CustomEvent('modal:opened', { 
      detail: { modal: this.modal } 
    });
    document.dispatchEvent(event);
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    
    // Remove focus from any element inside the modal before setting aria-hidden
    if (document.activeElement && this.modal.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    
    // Update ARIA attributes
    this.modal.setAttribute('aria-hidden', 'true');
    
    // Play closing animation
    this.timeline.reverse().then(() => {
      this.modal.style.display = 'none';
    });
    
    // Start Lenis scrolling
    if (window.lenis) {
      window.lenis.start();
    }
    
    // Return focus to previously focused element
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
    
    // Dispatch custom event
    const event = new CustomEvent('modal:closed', { 
      detail: { modal: this.modal } 
    });
    document.dispatchEvent(event);
  }

  handleKeyDown(e) {
    if (!this.isOpen) return;
    
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
        
      case 'Tab':
        this.handleTabKey(e);
        break;
    }
  }

  handleTabKey(e) {
    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];
    
    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  handleBackdropClick(e) {
    // Only close if clicking the backdrop (not the wrapper)
    if (e.target === this.modal) {
      this.close();
    }
  }

  trapFocus() {
    // Focus the first focusable element
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  destroy() {
    // Clean up event listeners
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.close);
    }
    
    document.removeEventListener('keydown', this.handleKeyDown);
    this.modal.removeEventListener('click', this.handleBackdropClick);
    
    // Clean up trigger event listeners
    if (this.triggers) {
      this.triggers.forEach(trigger => {
        trigger.removeEventListener('click', this.open);
      });
    }
    
    // Kill timeline
    if (this.timeline) {
      this.timeline.kill();
    }
    
    // Start Lenis scrolling if modal was open
    if (this.isOpen && window.lenis) {
      window.lenis.start();
    }
  }
}
